<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Confirm-Dialog
 *
 * Shows a dialog with a title, text, and a cancel / confirm button.
 *
 * You can override the title, content and action section by providing
 * own slots (title, default, actions).
 *
 * Emit events:
 * - 'no': when user clicks on cancel
 * - 'yes': when user clicks on ok
 */

const modelValue = defineModel<boolean>('modelValue')

const {
    title = null,
    text = null,
    yesText = null,
    noText = null,
    loading = false,
    closeOnYes = true,
    closeOnNo = true,
    disableYes = false,
    showNoButton = true,
    maxWidth = 300,
} = defineProps<{
    title?: string
    text?: string
    yesText?: string
    noText?: string
    loading?: boolean
    closeOnYes?: boolean
    closeOnNo?: boolean
    disableYes?: boolean
    showNoButton?: boolean
    maxWidth?: string | number
}>()

const { t } = useI18n()

const p_title = computed(() => {
    return title || t('bestaetigt')
})
const p_text = computed(() => {
    return text || '<message>'
})
const p_yesText = computed(() => {
    return yesText || t('yes')
})
const p_noText = computed(() => {
    return noText || t('no')
})

const emit = defineEmits(['no', 'yes', 'input'])
function onNo() {
    emit('no')
    if (closeOnNo) {
        modelValue.value = false
    }
}
function onYes() {
    emit('yes')
    if (closeOnYes) {
        modelValue.value = false
    }
}
</script>

<template>
    <v-dialog
        v-model:model-value="modelValue"
        :max-width="maxWidth"
        @click:outside="$emit('input', false)"
    >
        <v-card>
            <slot name="title">
                <v-card-title class="headline">{{ p_title }}</v-card-title>
            </slot>
            <slot>
                <v-card-text>{{ p_text }}</v-card-text>
            </slot>
            <slot name="actions">
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="showNoButton"
                        color="decline"
                        text
                        :disabled="loading"
                        @click="onNo"
                    >
                        {{ p_noText }}
                    </v-btn>

                    <v-btn
                        variant="flat"
                        color="action"
                        :loading="loading"
                        :disabled="disableYes"
                        @click="onYes"
                    >
                        {{ p_yesText }}
                    </v-btn>
                </v-card-actions>
            </slot>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.v-card__text,
.v-card__title {
    word-break: normal !important;
}
</style>
